// Phase 1 deterministic stub. Accepts the FULL address object — the contract is
// address-based, not ZIP-based. The stub may internally derive mock numbers
// however it likes, but the persisted snapshot always carries the full address.

import type { TaxLookupAddress, TaxLookupFn, TaxLookupResult } from './taxLookup';

const STATE_BASE_RATES: Record<string, number> = {
  CA: 0.0725, NY: 0.04, TX: 0.0625, FL: 0.06, WA: 0.065,
  OR: 0.0, NV: 0.0685, AZ: 0.056, CO: 0.029, IL: 0.0625,
};

function deterministicLocalRate(postalCode: string): number {
  // Derive a stable 0.000–0.025 local component from the postal code digits
  const digits = (postalCode.match(/\d/g) || []).join('').slice(0, 5);
  const seed = parseInt(digits || '0', 10) || 0;
  return Math.round((seed % 250)) / 10000; // 0.0000–0.0249
}

export const stubLookup: TaxLookupFn = async (addr: TaxLookupAddress): Promise<TaxLookupResult> => {
  // Simulate network latency for realistic UX testing
  await new Promise((r) => setTimeout(r, 350));

  const stateKey = (addr.state || '').trim().toUpperCase().slice(0, 2);
  const stateRate = STATE_BASE_RATES[stateKey] ?? 0.05;
  const localRate = deterministicLocalRate(addr.postalCode);
  const combinedRate = Number((stateRate + localRate).toFixed(4));

  return {
    combinedRate,
    stateRate,
    localRate,
    sourceLabel: 'stub',
    rawPayload: {
      provider: 'stub',
      requestedAddress: addr,
      derivedRates: { stateRate, localRate, combinedRate },
      note: 'Phase 1 deterministic stub — replace with TaxJar/Avalara in later phase',
    },
    lookedUpAt: new Date().toISOString(),
  };
};
